import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, Trash2, Mail, Phone, Building, Globe, Linkedin, Briefcase, Inbox, Layers } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });

export const TYPE_META = {
  contact: { label: 'İletişim', cls: 'bg-[#C8AA73]/15 text-[#E8D6AF] border-[#C8AA73]/30' },
  demo: { label: 'Demo', cls: 'bg-sky-400/10 text-sky-200 border-sky-300/25' },
  career: { label: 'Kariyer', cls: 'bg-emerald-400/10 text-emerald-200 border-emerald-300/25' },
};
const FILTERS = [['all', 'Tümü'], ['contact', 'İletişim'], ['demo', 'Demo'], ['career', 'Kariyer'], ['unread', 'Okunmamış']];
const fmt = (d) => new Date(d).toLocaleString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const subtitle = (m) => (m.type === 'career' ? m.position : m.company) || '';

const TypeBadge = ({ type }) => (
  <span className={`px-2 py-0.5 text-[11px] rounded-full border ${TYPE_META[type].cls}`} data-testid={`inbox-type-${type}`}>{TYPE_META[type].label}</span>
);

const Row = ({ m, active, onOpen }) => (
  <button
    onClick={() => onOpen(m)}
    className={`w-full text-left p-4 rounded-xl border transition-colors ${active ? 'bg-[#C8AA73]/10 border-[#C8AA73]/30' : 'bg-[#121212] border-white/10 hover:border-white/20'} ${!m.is_read ? 'border-l-4 border-l-[#C8AA73]' : ''}`}
    data-testid={`inbox-item-${m.id}`}
  >
    <div className="flex items-center gap-2 mb-1">
      <TypeBadge type={m.type} />
      <span className="font-semibold text-white truncate">{m.name}</span>
      {!m.is_read && <span className="w-2 h-2 rounded-full bg-[#C8AA73]" data-testid="inbox-unread-dot" />}
      <span className="ml-auto text-xs text-white/30 whitespace-nowrap">{fmt(m.created_at)}</span>
    </div>
    <p className="text-sm text-white/50 truncate">{m.email}{subtitle(m) ? ` · ${subtitle(m)}` : ''}</p>
    {m.message && <p className="text-sm text-white/40 mt-1 line-clamp-1">{m.message}</p>}
  </button>
);

const Field = ({ icon: Icon, children }) => (children ? <div className="flex items-start gap-3 text-white/70"><Icon className="w-5 h-5 text-[#C8AA73] shrink-0 mt-0.5" /><span className="break-all">{children}</span></div> : null);

const Detail = ({ m, onDelete }) => (
  <div className="bg-[#121212] border border-white/10 rounded-xl p-6 lg:sticky lg:top-24" data-testid="inbox-detail">
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        <TypeBadge type={m.type} />
        <h2 className="text-xl font-bold text-white mt-2" style={{ fontFamily: 'var(--font-display)' }}>{m.name}</h2>
        <p className="text-white/50 text-sm">{fmt(m.created_at)}</p>
      </div>
      <Button variant="ghost" size="sm" onClick={() => onDelete(m)} className="text-red-400 hover:text-red-300 hover:bg-red-400/10" data-testid="inbox-delete-btn"><Trash2 className="w-4 h-4" /></Button>
    </div>
    <div className="space-y-3">
      <Field icon={Mail}><a href={`mailto:${m.email}`} className="hover:text-[#C8AA73]">{m.email}</a></Field>
      <Field icon={Phone}>{m.phone}</Field>
      <Field icon={Building}>{m.company}</Field>
      <Field icon={Briefcase}>{m.position}</Field>
      <Field icon={Layers}>{m.services && m.services.length ? m.services.join(', ') : null}</Field>
      <Field icon={Linkedin}>{m.linkedin}</Field>
      <Field icon={Globe}>{m.language ? m.language.toUpperCase() : null}</Field>
    </div>
    {m.message && (
      <div className="mt-6 pt-6 border-t border-white/10">
        <h3 className="text-sm font-semibold text-white/60 mb-3">Mesaj</h3>
        <p className="text-white/80 whitespace-pre-wrap leading-relaxed" data-testid="inbox-detail-message">{m.message}</p>
      </div>
    )}
    <a href={`mailto:${m.email}`} className="block mt-6"><Button className="btn-primary w-full" data-testid="inbox-reply-btn"><Mail className="w-4 h-4 mr-2" />Yanıtla</Button></a>
  </div>
);

const AdminInbox = () => {
  const { stats, refreshStats } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/inbox`, auth()).then((r) => setItems(r.data)).catch(() => toast.error('Mesajlar yüklenemedi')).finally(() => setLoading(false));
  }, []);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((m) => (filter === 'all' || (filter === 'unread' ? !m.is_read : m.type === filter))
      && (!s || [m.name, m.email, m.company, m.position, m.message].some((v) => v && v.toLowerCase().includes(s))));
  }, [items, filter, q]);

  const open = (m) => {
    setSel(m);
    if (m.is_read) return;
    axios.patch(`${API}/admin/inbox/${m.type}/${m.id}/read`, {}, auth()).then(() => {
      setItems((xs) => xs.map((x) => (x.id === m.id ? { ...x, is_read: true } : x)));
      refreshStats();
    }).catch(() => {});
  };

  const remove = async (m) => {
    if (!window.confirm('Bu mesajı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/inbox/${m.type}/${m.id}`, auth());
      setItems((xs) => xs.filter((x) => x.id !== m.id));
      setSel(null);
      refreshStats();
      toast.success('Mesaj silindi');
    } catch (e) { toast.error('Silinemedi'); }
  };

  const count = (k) => (k === 'all' ? items.length : k === 'unread' ? items.filter((m) => !m.is_read).length : items.filter((m) => m.type === k).length);

  return (
    <div data-testid="admin-inbox">
      <div className="flex flex-wrap items-end gap-6 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'var(--font-display)' }}>Gelen Kutusu</h1>
          <p className="text-white/50 mt-2">Tüm iletişim, demo ve kariyer mesajları tek yerde</p>
        </div>
        <div className="flex gap-3 ml-auto">
          <div className="px-4 py-2 rounded-xl bg-[#121212] border border-white/10" data-testid="inbox-total-count"><p className="text-xs text-white/40">Toplam</p><p className="text-xl text-white">{stats?.inbox?.total ?? items.length}</p></div>
          <div className="px-4 py-2 rounded-xl bg-[#C8AA73]/10 border border-[#C8AA73]/25" data-testid="inbox-unread-count"><p className="text-xs text-[#C8AA73]/80">Okunmamış</p><p className="text-xl text-[#E8D6AF]">{stats?.inbox?.unread ?? count('unread')}</p></div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${filter === k ? 'bg-[#C8AA73] text-black border-[#C8AA73]' : 'text-white/60 border-white/10 hover:text-white hover:border-white/25'}`} data-testid={`inbox-filter-${k}`}>
              {label} <span className="opacity-60">{count(k)}</span>
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-72 sm:ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad, e-posta, şirket, mesaj ara…" className="!pl-9 input-dark" data-testid="inbox-search" />
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-white/40">Yükleniyor…</div>
      ) : shown.length === 0 ? (
        <div className="text-center py-20 bg-[#121212] rounded-xl border border-white/10" data-testid="inbox-empty"><Inbox className="w-12 h-12 text-white/20 mx-auto mb-4" /><p className="text-white/50">Mesaj bulunamadı</p></div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <div className="space-y-3" data-testid="inbox-list">{shown.map((m) => <Row key={`${m.type}-${m.id}`} m={m} active={sel && sel.id === m.id} onOpen={open} />)}</div>
          {sel ? <div className="order-first lg:order-none"><Detail m={sel} onDelete={remove} /></div> : <div className="hidden lg:flex bg-[#121212] border border-white/10 rounded-xl p-6 items-center justify-center min-h-[300px] text-white/40">Detayları görmek için bir mesaj seçin</div>}
        </div>
      )}
    </div>
  );
};

export default AdminInbox;
